/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useLoading } from "../../../Components/Common/LoadingSpinner/LoadingContext";
import { useAppContext } from "../../../AppContext";
import {
  getImageBlobFromDB,
  updateLastUpdateTime,
  getLastUpdateTime,
  saveImageBlobToDB,
  getAllKeysFromDB,
  clearStore,
  deleteSelectedImages,
  FACE_STORE_NAME,
} from "../../../IndexedDBUtils";
import { base64ToBlob } from "../../../generalUtils";
import { SERVER_URL } from "../../../config/serverUrl";
import { getEventScope, isCurrentScope } from "../../../eventIdentity";
import { formatSeconds, useFlowTimeLeft } from "./useFlowInactivity";
import { DEFAULT_FETCH_LIMIT, fetchWithLimit } from "./fetchWithLimit";

// Configuration constants
export const FACE_REFRESH_INTERVAL_SECONDS = 6; // Polling interval for image updates (seconds)

// Admin live preview: the console embeds the gallery pages with ?admin_preview=1
// to mirror EXACTLY what guests see in the gallery — without the header,
// timer, buttons or the idle-return mechanism.
export const isAdminPreview = () =>
  new URLSearchParams(window.location.search).get("admin_preview") === "1";

/**
 * Shared engine behind the guest-facing face galleries (Swingies / MiniMove).
 *
 * Owns: eventName resolution, admin-preview detection, the 6s /api/faces
 * polling loop with visibility guard, and the IndexedDB load/save flow (incl.
 * the per-key object-URL cache with stale-key revocation and unmount cleanup).
 * A failed list read keeps the current list (`syncError`); only a 204 or an
 * empty list empties it (`isEmpty`).
 *
 * The 90 s inactivity deadline is NOT owned here any more: it is one wall-clock
 * deadline for the whole guest flow (useFlowInactivity, wired in App.js), and
 * this hook only DISPLAYS what is left of it.
 *
 * @param {Object}  options
 * @param {number}  options.maxSelectable    Max faces the guest may select.
 * @param {boolean} [options.hideLoadingOnInit=false]
 *                  Hide the global spinner immediately on mount (MiniMove
 *                  behavior) instead of only after the first image load
 *                  (Swingies behavior).
 * @param {Function} [options.onCycle]       Runs on every 6 s load cycle (the
 *                  gallery refreshes the "בעריכה" held list there).
 * @param {Function} [options.isBlocked]     (path) => true when a face must not
 *                  be selectable (held by another guest).
 */
export const useFaceGalleryEngine = ({
  maxSelectable,
  hideLoadingOnInit = false,
  onCycle,
  isBlocked,
}) => {
  const adminPreview = isAdminPreview();
  const serverUrl = SERVER_URL;
  const { hideLoading: hideLoadingGlobal } = useLoading();
  // hideLoading only ever clears the shared overlay on behalf of a LIVE page of
  // the CURRENT event visit: a late load from an unmounted page (or from event
  // A after the guest moved to B) must not hide the loading another page or
  // event is showing. The engine owns no loading token of its own (it only
  // dismisses the overlay raised by the previous page's navigation).
  const scopeRef = useRef(getEventScope());
  const mountedRef = useRef(true);
  // True while the event visit this page was opened for is still the active one.
  const isCurrentVisit = () => {
    const scope = scopeRef.current;
    return !scope || isCurrentScope(scope);
  };
  const hideLoading = () => {
    if (!mountedRef.current || !isCurrentVisit()) return;
    hideLoadingGlobal();
  };
  const { eventName: contextEventName } = useAppContext();
  // Deep-loads (e.g. the admin live preview iframe) land here without
  // passing through Home; EventRouteGate has already validated the route name
  // and set both the context and the scope, so the scope is the only fallback
  // (never a second, URL-derived truth).
  const eventName = contextEventName || getEventScope()?.name;

  // Component state
  const [imagePaths, setImagePaths] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  // The last /api/faces poll failed (non-OK, unreadable body or network), or
  // some of its new faces could not be downloaded: the current list stays and
  // the gallery shows a small "מתעדכן…" chip until a poll completes.
  const [syncError, setSyncError] = useState(false);
  // The server answered "no faces" (204 or []): the gallery shows an explicit
  // empty state instead of a blank grid.
  const [serverEmpty, setServerEmpty] = useState(false);

  // Remaining time of the shared flow deadline (display only)
  const timeLeft = useFlowTimeLeft();
  const callbacksRef = useRef({ onCycle, isBlocked });
  callbacksRef.current = { onCycle, isBlocked };

  // Refs to manage intervals and component state
  const pollingIntervalRef = useRef(null);
  const isActiveRef = useRef(true);
  // Object URLs per IndexedDB key, reused across polls — creating a fresh
  // URL every 6s leaked blob URLs in the always-open admin preview.
  const objectUrlsRef = useRef(new Map());
  // The server sync in flight (fetchDataFromServer is single-flight).
  const syncRef = useRef(null);
  // Aborted on unmount: stops the capped face downloads (no new /image request
  // starts, the ones in flight are cancelled) and the /api/faces read. One per
  // mount, so a StrictMode replay's first mount cannot revive its own work.
  const abortRef = useRef(null);

  // Cleanup intervals on unmount
  useEffect(() => {
    mountedRef.current = true;
    abortRef.current = new AbortController();
    return () => {
      mountedRef.current = false;
      abortRef.current.abort();
      // A remount (StrictMode) starts its own sync instead of joining this one.
      syncRef.current = null;
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  // Guard for one load cycle: `live()` is true while the mount that started the
  // cycle is still mounted and its event visit is still current. Every await
  // in the cycle is followed by `live()` before it touches IndexedDB, state,
  // object URLs or the network: a page that unmounted (or whose visit moved to
  // another event) makes no further request and writes nothing.
  const startCycle = () => {
    if (!abortRef.current) abortRef.current = new AbortController();
    const { signal } = abortRef.current;
    return { signal, live: () => !signal.aborted && isCurrentVisit() };
  };

  // Manage page visibility: pause/resume the image polling while hidden. The flow deadline is wall-clock
  // and is deliberately NOT paused here (hidden time counts).
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        if (pollingIntervalRef.current)
          clearInterval(pollingIntervalRef.current);
      } else {
        isActiveRef.current = true;
        startPollingInterval();
        loadImages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Initial setup effect
  useEffect(() => {
    // Clean up previously stored full face images
    const cleanupPreviousImages = () => {
      const faceUrls = JSON.parse(
        sessionStorage.getItem("faceImageBlobUrls") || "{}"
      );
      Object.values(faceUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      sessionStorage.removeItem("faceImageBlobUrls");
      sessionStorage.removeItem("faceImagesDownloadStatus");
    };

    cleanupPreviousImages();
    loadImages();
    startPollingInterval();
    if (hideLoadingOnInit) hideLoading();
  }, []);

  // Start the polling timer to refresh images from server or DB
  const startPollingInterval = () => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    pollingIntervalRef.current = setInterval(() => {
      loadImages();
    }, FACE_REFRESH_INTERVAL_SECONDS * 1000);
  };

  // Format remaining time as M:SS
  const formatTimeLeft = () => formatSeconds(timeLeft);

  // Load images from IndexedDB or the server based on last update time
  const loadImages = async () => {
    if (!isActiveRef.current) return;
    const cycle = startCycle();
    if (!cycle.live()) return;
    // The held ("בעריכה") list rides on this same cycle.
    const { onCycle } = callbacksRef.current;
    if (onCycle) onCycle();
    const lastUpdateTime = await getLastUpdateTime(eventName, FACE_STORE_NAME);
    if (!cycle.live()) return;
    const currentTime = new Date();
    const timeDifference = (currentTime - new Date(lastUpdateTime)) / 1000;

    if (timeDifference < FACE_REFRESH_INTERVAL_SECONDS) {
      await loadImagesFromDB(cycle);
    } else {
      console.warn("Checking for updates from server...");
      await fetchDataFromServer(cycle);
    }
  };

  // Read the saved faces from IndexedDB with validation, as { path, image }
  // with one cached object URL per key. Keys are loaded in parallel but the
  // result keeps IndexedDB key order. An entry with no image or an invalid
  // one is dropped from the store: its key would otherwise count as stored
  // forever (never "new" to a sync), leaving a hole no poll ever fills.
  // null when the cycle is no longer live: no object URL is minted then (the
  // unmount cleanup already revoked and cleared the cache, so nothing would
  // ever revoke it).
  const readImagesFromDB = async ({ live }) => {
    const allKeys = await getAllKeysFromDB(eventName, FACE_STORE_NAME);
    if (!live()) return null;
    const unusable = [];

    // Revoke object URLs whose keys were removed from IndexedDB
    const liveKeys = new Set(allKeys);
    objectUrlsRef.current.forEach((url, path) => {
      if (!liveKeys.has(path)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(path);
      }
    });

    const entries = await Promise.all(
      allKeys.map(async (path) => {
        try {
          // Reuse the cached object URL across polls when available
          let imageUrl = objectUrlsRef.current.get(path);
          if (!imageUrl) {
            const imageBlob = await getImageBlobFromDB(
              eventName,
              FACE_STORE_NAME,
              path
            );
            if (!live()) return null;

            if (!imageBlob) {
              console.warn(`No data found for ${path} in IndexedDB`);
              unusable.push(path);
              return null;
            }

            // Enhanced validation: Check blob integrity
            let blobToUse;
            if (typeof imageBlob === "string") {
              blobToUse = await base64ToBlob(imageBlob, "image/webp");
            } else {
              blobToUse = imageBlob;
            }

            // Validate blob size and type (allows empty type for compatibility)
            if (
              !blobToUse ||
              blobToUse.size === 0 ||
              (blobToUse.type && !blobToUse.type.startsWith("image/"))
            ) {
              console.warn(`Invalid blob data for ${path}, skipping`);
              unusable.push(path);
              return null;
            }

            if (!live()) return null;
            imageUrl = URL.createObjectURL(blobToUse);
            objectUrlsRef.current.set(path, imageUrl);
          }
          return { path, image: imageUrl };
        } catch (error) {
          console.error(`Error loading image ${path} from IndexedDB:`, error);
          return null;
        }
      })
    );
    if (!live()) return null;
    if (unusable.length > 0) {
      try {
        await deleteSelectedImages(eventName, FACE_STORE_NAME, unusable);
      } catch (error) {
        console.error("Error dropping unusable faces:", error);
      }
      if (!live()) return null;
    }
    return entries.filter(Boolean);
  };

  // Load images from IndexedDB; an empty (or unusable) store asks the server.
  const loadImagesFromDB = async (cycle) => {
    const dbImages = await readImagesFromDB(cycle);
    if (!dbImages) return;

    if (dbImages.length > 0) {
      setImagePaths(dbImages);
    } else {
      console.warn(
        "No usable images found in IndexedDB, fetching from server..."
      );
      await fetchDataFromServer(cycle);
    }

    hideLoading();
  };

  // After a server sync the grid shows what IndexedDB now holds (blob URLs),
  // never server URLs: pointing <img src> at /image re-requested every face on
  // each list change (each face file 6-7 times in 5 minutes on 2026-10-06).
  // false when the cycle is no longer live (nothing rendered).
  const renderFromDB = async (cycle) => {
    const dbImages = await readImagesFromDB(cycle);
    if (!dbImages) return false;
    setImagePaths(dbImages);
    return true;
  };

  // Download one new face thumbnail into IndexedDB (validated and verified).
  // Starts no request once the cycle is no longer live, and the request in
  // flight is aborted on unmount; a late answer is never saved.
  const downloadFace = async (path, { live, signal }) => {
    if (!live()) return false;
    try {
      const response = await fetch(`${serverUrl}/image${path}?size=thumbnail`, {
        signal,
      });
      if (!live()) return false;
      if (!response.ok) {
        console.error(`Failed to fetch image for path: ${path}`);
        return false;
      }

      const blob = await response.blob();
      if (!live()) return false;

      // Enhanced validation: Verify blob integrity before saving (allows empty type)
      if (
        !blob ||
        blob.size === 0 ||
        (blob.type && !blob.type.startsWith("image/"))
      ) {
        console.error(`Invalid blob received for path: ${path}`);
        return false;
      }

      await saveImageBlobToDB(eventName, FACE_STORE_NAME, path, blob);

      // Verify save was successful
      const savedBlob = await getImageBlobFromDB(
        eventName,
        FACE_STORE_NAME,
        path
      );
      if (!savedBlob) {
        console.error(`Failed to verify saved image for path: ${path}`);
        return false;
      }

      return true;
    } catch (error) {
      // An abort on unmount is expected, not a failure worth reporting.
      if (live()) console.error(`Error processing image ${path}:`, error);
      return false;
    }
  };

  // Fetch images from server and update IndexedDB accordingly. Single-flight:
  // a call while a sync is still running (downloads are capped, so a sync can
  // outlast the 6 s tick) joins it instead of starting a second one.
  const fetchDataFromServer = (cycle) => {
    if (!syncRef.current) {
      const sync = syncWithServer(cycle).finally(() => {
        if (syncRef.current === sync) syncRef.current = null;
      });
      syncRef.current = sync;
    }
    return syncRef.current;
  };

  const syncWithServer = async (cycle) => {
    const { live, signal } = cycle;
    if (!eventName) {
      console.error("FaceGallery - eventName is missing.");
      return;
    }
    const encodedEventName = encodeURIComponent(eventName);
    try {
      const response = await fetch(
        `${serverUrl}/api/faces?event_name=${encodedEventName}`,
        { signal }
      );
      if (!live()) return;
      if (response.status === 204) {
        console.warn("No images found on server. Clearing local storage.");
        await showNoFaces(cycle);
        console.warn("Cleared IndexedDB.");
        return;
      }
      if (!response.ok) {
        // A failed read is not an empty event: the JSON 503
        // {"error":"cache_unavailable"} (or an HTML 500, or a 400) keeps the
        // heads the guest is looking at and shows "מתעדכן…" until the next
        // OK poll. Parsing such a body as a list used to wipe the gallery.
        if (response.status === 400) {
          console.error("Bad request received. Keeping the current list.");
        } else {
          console.warn(
            `Face list unavailable (HTTP ${response.status}); keeping the current list.`
          );
        }
        setSyncError(true);
        return;
      }
      const data = await response.json();
      if (!live()) return;
      if (!Array.isArray(data)) {
        console.warn("Unexpected face list body; keeping the current list.");
        setSyncError(true);
        return;
      }
      if (data.length === 0) {
        console.warn("Server returned an empty list. Clearing local data.");
        await showNoFaces(cycle);
        return;
      }
      setServerEmpty(false);
      const keysFromDB = await getAllKeysFromDB(eventName, FACE_STORE_NAME);
      if (!live()) return;
      const newPaths = data.filter((path) => !keysFromDB.includes(path));
      const pathsToDelete = keysFromDB.filter((path) => !data.includes(path));
      if (newPaths.length === 0 && pathsToDelete.length === 0) {
        console.warn("No changes in paths, skipping server fetch.");
        // Render only (no server fallback from inside this sync).
        if (!(await renderFromDB(cycle))) return;
        setSyncError(false);
        hideLoading();
        updateLastUpdateTime(eventName, FACE_STORE_NAME);
        return;
      }
      await deleteSelectedImages(eventName, FACE_STORE_NAME, pathsToDelete);
      if (!live()) return;
      // Faces arrive one by one, so new ones are fetched per file, but never
      // more than 6 at a time (fetchWithLimit's default). A face that failed
      // stays out of the grid and is retried on the next poll; until then the
      // "מתעדכן…" chip says the gallery is not complete yet. Unmount aborts
      // the signal: no further face starts and the ones in flight stop.
      const downloads = await fetchWithLimit(
        newPaths,
        (path) => downloadFace(path, cycle),
        DEFAULT_FETCH_LIMIT,
        { signal }
      );
      if (!live()) return;
      const allStored = downloads.every(
        (result) => result.status === "fulfilled" && result.value === true
      );
      if (!(await renderFromDB(cycle))) return;
      setSyncError(!allStored);
      // Functional update: this runs from a stale interval closure, so it must not read `selectedImages`.
      setSelectedImages((current) => current.filter((img) => data.includes(img)));
      updateLastUpdateTime(eventName, FACE_STORE_NAME);
    } catch (error) {
      // An abort on unmount (or any late failure of a page that is gone) is
      // not a failed read: there is nothing left to show it on.
      if (!live()) return;
      // Network failure or an unreadable body: same as a failed read.
      console.error("Error fetching faces:", error);
      setSyncError(true);
    } finally {
      hideLoading();
    }
  };

  // The server has no faces right now (204 or an empty list): a genuine empty
  // state, said in words instead of a blank grid.
  const showNoFaces = async ({ live }) => {
    await clearStore(eventName, FACE_STORE_NAME);
    if (!live()) return;
    setImagePaths([]);
    setSelectedImages([]);
    setSyncError(false);
    setServerEmpty(true);
  };

  // Toggle image selection. Functional update (no stale closure) and a face held by another guest can
  // never be added, whichever handler called this (the tile guard is only the first line of defence).
  const toggleImageSelection = (imagePath) => {
    setSelectedImages((current) => {
      if (current.includes(imagePath)) {
        return current.filter((img) => img !== imagePath);
      }
      const { isBlocked: blocked } = callbacksRef.current;
      if (blocked && blocked(imagePath)) return current;
      if (current.length < maxSelectable) {
        return [...current, imagePath];
      }
      return current;
    });
  };

  return {
    adminPreview,
    eventName,
    serverUrl,
    imagePaths,
    selectedImages,
    setSelectedImages,
    timeLeft,
    formatTimeLeft,
    toggleImageSelection,
    syncError,
    isEmpty: serverEmpty && imagePaths.length === 0,
  };
};
